import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ConfigProvider, Segmented, Select, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faHouse, faHouseCircleCheck, faLock, faUser } from '@fortawesome/free-solid-svg-icons';
import { P, fs, fontSans, radius, onPhone, phoneControls, scrollRow } from '../../theme/tokens';
import { usePhone } from '../../hooks/usePhone';
import { noLetters } from '../../components/Avatar';

export const cx = (...c) => c.filter(Boolean).join(' ');

export const I = ({ icon }) => (
  <i aria-hidden="true">
    <FontAwesomeIcon icon={icon} />
  </i>
);

export const Chip = ({ fam = 'neutral', icon, className, children }) => (
  <span className={cx('m-chip', `c-${fam}`, className)}>
    {icon && <I icon={icon} />}
    {children}
  </span>
);

export function Btn({ children, icon, kind, sm, block, lock, className, ...rest }) {
  return (
    <button
      type="button"
      className={cx('m-btn', kind, sm && 'sm', block && 'block', className)}
      disabled={rest.disabled || Boolean(lock)}
      title={lock || rest.title}
      aria-label={lock ? `${children}. Locked: ${lock}` : rest['aria-label']}
      {...rest}
    >
      {lock ? <I icon={faLock} /> : icon ? <I icon={icon} /> : null}
      {children}
    </button>
  );
}

export const LockBtn = ({ label, why, sm }) => (
  <span className="m-lockw">
    <Btn sm={sm} lock={why}>
      {label}
    </Btn>
    <span className="m-why">{why}</span>
  </span>
);

const themeFor = (control, extra, r = control / 2) => ({
  token: { controlHeight: control, fontSize: fs.sm, colorBorder: P.border, colorPrimary: P.navyInk, colorPrimaryHover: P.navy, ...extra },
  components: {
    Segmented: {
      trackBg: P.white,
      trackPadding: 3,
      itemColor: P.muted,
      itemHoverColor: P.ink,
      itemHoverBg: P.gray100,
      itemSelectedBg: P.navyInk,
      itemSelectedColor: P.white,
      borderRadius: r,
      borderRadiusSM: r - 3
    },
    Select: {
      borderRadius: r,
      optionSelectedBg: '#EEF3FA',
      optionSelectedColor: P.navyInk,
      optionActiveBg: P.gray100,
      optionSelectedFontWeight: 600
    },
    Input: { borderRadius: r, paddingInline: 14 }
  }
});
const THEME = themeFor(36);
const PHONE_THEME = themeFor(44, { ...phoneControls, controlHeight: 44, fontFamily: fontSans }, 8);

export const AaTheme = ({ children }) => <ConfigProvider theme={usePhone() ? PHONE_THEME : THEME}>{children}</ConfigProvider>;

const Pill = styled(Segmented)`
  &.ant-segmented {
    box-shadow: inset 0 0 0 1px ${P.border};
  }
  .ant-segmented-item-label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding-inline: 14px;
  }
  .ant-segmented-item-icon {
    display: inline-flex;
    font-size: 12px;
  }
  .ant-segmented-item-icon + * {
    margin-inline-start: 0;
  }
  .ant-segmented-item-selected,
  .ant-segmented-thumb {
    background: ${P.grad};
    font-weight: 600;
  }
  .aa-cnt {
    padding: 0 6px;
    line-height: 18px;
    border-radius: ${radius.pill}px;
    background: rgba(21, 38, 66, 0.08);
    font-size: 11px;
    font-weight: 700;
  }
  .ant-segmented-item-selected .aa-cnt {
    background: rgba(255, 255, 255, 0.22);
  }
  ${onPhone} {
    &.ant-segmented {
      max-width: 100%;
      vertical-align: top;
      padding: 0;
      border: 1px solid ${P.border};
      border-radius: 8px;
      box-shadow: none;
      font-family: ${fontSans};
      ${scrollRow}
    }
    .ant-segmented-group {
      min-width: max-content;
    }
    .ant-segmented-item,
    .ant-segmented-item::after,
    .ant-segmented-thumb {
      border-radius: 0;
    }
    .ant-segmented-item-label {
      min-height: 44px;
      line-height: 44px;
      padding-inline: 12px;
      font-size: ${fs.base}px;
      font-weight: 600;
    }
    .aa-cnt {
      font-size: 12px;
    }
  }
`;

const STILL = { token: { motion: false } };

export function Seg({ items, value, onChange, label, block }) {
  const ref = useRef(null);
  useEffect(() => {
    const box = ref.current;
    const sel = box?.querySelector('.ant-segmented-item-selected');
    if (!sel || box.scrollWidth <= box.clientWidth) return;
    box.scrollLeft += sel.getBoundingClientRect().left - box.getBoundingClientRect().left - (box.clientWidth - sel.offsetWidth) / 2;
  }, [value]);
  return (
    <ConfigProvider theme={STILL}>
      <Pill
        ref={ref}
        aria-label={label}
        block={block}
        value={value}
        onChange={onChange}
        options={items.map(([key, text, icon, count, tip]) => {
          const body =
            count === undefined ? (
              text
            ) : (
              <>
                {text}
                <span className="aa-cnt num">{count}</span>
              </>
            );
          return {
            value: key,
            title: tip ? undefined : text,
            icon: icon ? <FontAwesomeIcon icon={icon} /> : undefined,
            label: tip ? (
              <Tooltip title={tip} mouseEnterDelay={0.3}>
                <span className="aa-tip">{body}</span>
              </Tooltip>
            ) : (
              body
            )
          };
        })}
      />
    </ConfigProvider>
  );
}

export const PillSelect = ({ icon, tip, ...rest }) => (
  <Tooltip title={tip} mouseEnterDelay={0.3}>
    <Select
      prefix={<FontAwesomeIcon icon={icon} style={{ color: P.muted }} />}
      suffixIcon={<FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 11, color: P.muted, pointerEvents: 'none' }} />}
      popupMatchSelectWidth={false}
      style={{ width: 180 }}
      {...rest}
    />
  </Tooltip>
);

export const ColSort = (props) => (
  <Select
    className="aa2-sort"
    variant="borderless"
    size="small"
    popupMatchSelectWidth={false}
    suffixIcon={<FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 9, color: P.muted, pointerEvents: 'none' }} />}
    {...props}
  />
);

export function Av({ name, cls }) {
  if (noLetters(name)) {
    return (
      <span className={cx('m-av', cls)}>
        <FontAwesomeIcon icon={faUser} />
      </span>
    );
  }
  const parts = String(name || '?')
    .replace(/\(.*\)/, '')
    .trim()
    .split(/\s+/);
  return <span className={cx('m-av', cls)}>{((parts[0] || '?')[0] + (parts[1] ? parts[1][0] : '')).toUpperCase()}</span>;
}

const reduced = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const CONFETTI = ['#52C41A', 'var(--brand-primary)', '#FAAD14', '#1890FF'];

export function House({ party, done, onDrop }) {
  const [hover, setHover] = useState(false);
  const conf =
    party && !reduced()
      ? Array.from({ length: 8 }, (_, i) => {
          const a = (i / 8) * Math.PI * 2;
          return (
            <span
              key={i}
              className="m-conf"
              style={{
                '--dx': `${Math.round(Math.cos(a) * 70)}px`,
                '--dy': `${Math.round(Math.sin(a) * 60 - 20)}px`,
                background: CONFETTI[i % 4],
                animationDelay: `${i * 30}ms`
              }}
            />
          );
        })
      : null;
  return (
    <div
      className={cx('m-house', hover && 'hover', party && 'party')}
      role="region"
      aria-label="Done? Send it home. Drop a card here to finish it."
      onDragOver={(e) => {
        e.preventDefault();
        if (!hover) setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        const id = e.dataTransfer.getData('text/plain');
        if (id) onDrop(id);
      }}
    >
      <div className={cx('m-hicon', party && !reduced() && 'hop')}>
        <I icon={party ? faHouseCircleCheck : faHouse} />
        {conf}
      </div>
      <div className="b">{party ? 'House party! Filed under completed' : hover ? 'Let go to send it home' : 'Done? Send it home'}</div>
      <div className="m-sub">{party ? `Completed (${done})` : 'Drag a card here, or tick its Done box.'}</div>
      <div className="m-srlive" aria-live="polite">
        {party ? `Filed under completed. ${done} completed.` : ''}
      </div>
    </div>
  );
}
